(() => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  let context;
  let buttonBuffer;
  let decoding;
  const soundUrl = document.querySelector('script[data-atm-beep-src]')?.dataset.atmBeepSrc;
  const soundBytes = soundUrl ? fetch(soundUrl).then(response => {
    if (!response.ok) throw new Error('ATM beep unavailable');
    return response.arrayBuffer();
  }).catch(() => null) : Promise.resolve(null);
  const active = new Set();
  let soundEnabled = true;
  try { soundEnabled = localStorage.getItem('sm-atm-sound') !== 'off'; } catch (_) {}
  const sourceTag = document.querySelector('script[data-atm-beep-src]');
  const phaseBuffers = {};
  const phaseBytes = {};
  for (const phase of ['printing', 'receipt']) {
    const url = sourceTag?.dataset[phase === 'printing' ? 'atmPrintingSrc' : 'atmReceiptSrc'];
    if (url) phaseBytes[phase] = fetch(url).then(response => {
      if (!response.ok) throw new Error('ATM sound unavailable');
      return response.arrayBuffer();
    }).catch(() => null);
  }
  let phasesDecoding;
  const decodePhases = () => {
    if (phasesDecoding || !context) return;
    phasesDecoding = Promise.all(Object.entries(phaseBytes).map(([phase, bytes]) =>
      bytes.then(data => data && context.decodeAudioData(data)).then(buffer => {
        if (buffer) phaseBuffers[phase] = buffer;
      }).catch(() => {})
    ));
  };
  const unlock = () => {
    if (!context) context = new AudioContext();
    if (!decoding) decoding = soundBytes.then(bytes => bytes && context.decodeAudioData(bytes)).then(buffer => {
      if (buffer) {
        // Keep only the keypad tone from the supplied ATM recording.
        const offset = Math.round(0.125 * buffer.sampleRate);
        const length = Math.round(0.075 * buffer.sampleRate);
        buttonBuffer = context.createBuffer(buffer.numberOfChannels, length, buffer.sampleRate);
        for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
          const input = buffer.getChannelData(channel);
          const output = buttonBuffer.getChannelData(channel);
          for (let index = 0; index < length; index++) {
            const edge = Math.min(1, index / (buffer.sampleRate * 0.004), (length - 1 - index) / (buffer.sampleRate * 0.012));
            output[index] = (input[offset + index] || 0) * Math.max(0, edge);
          }
        }
        document.querySelectorAll('[data-smcc]').forEach(root => { root.dataset.atmButtonSound = 'trimmed-atm-keypad'; });
      }
    }).catch(() => {});

    decodePhases();
    if (context.state === 'suspended' || context.state === 'interrupted') return context.resume();
    return Promise.resolve();
  };
  document.querySelectorAll('[data-smcc]').forEach(root => {
    if (root.dataset.atmBeepBound) return;
    root.dataset.atmBeepBound = 'true';
    let lastControl = -Infinity;
    let blink = 0;
    const visible = () => !document.hidden && root.isConnected &&
      !root.closest('[hidden]') && root.getClientRects().length > 0;
    const ready = () => visible() &&
      root.classList.contains('atm-step-confirmed') &&
      !root.classList.contains('atm-await-done') &&
      root.querySelector('.atm22-next')?.classList.contains('ready') &&
      +root.dataset.builderStep < 6;
    const tone = kind => {
      if ((!soundEnabled && kind !== 'toggle') || !visible() || context?.state !== 'running') return;
      if (kind === 'reminder' && (!(ready() || root.classList.contains('atm-back-needed')) || performance.now() - lastControl < 1000)) return;
      if (kind !== 'reminder' && kind !== 'toggle' && !buttonBuffer) return;
      if (kind !== 'reminder' && kind !== 'toggle' && buttonBuffer) {
        const sample = context.createBufferSource();
        const level = context.createGain();
        sample.buffer = buttonBuffer;
        level.gain.value = 0.0288;
        sample.connect(level);
        level.connect(context.destination);
        active.add(sample);
        sample.onended = () => { active.delete(sample); sample.disconnect(); level.disconnect(); };
        sample.start();
        root.dataset.atmLastSound = kind;
        root.dataset.atmSoundCount = String((+root.dataset.atmSoundCount || 0) + 1);
        return;
      }
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const reminder = kind === 'reminder';
      const start = context.currentTime;
      const clicking = kind === 'toggle';
      const duration = clicking ? 0.025 : reminder ? 0.065 : 0.095;
      oscillator.type = clicking ? 'triangle' : 'sine';
      oscillator.frequency.setValueAtTime(clicking ? 180 : reminder ? 780 : 1050, start);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(clicking ? 0.12 : reminder ? 0.055 : 0.16, start + (clicking ? 0.002 : 0.008));
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      oscillator.connect(gain);
      gain.connect(context.destination);
      active.add(oscillator);
      oscillator.onended = () => { active.delete(oscillator); oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(start);
      oscillator.stop(start + duration + 0.01);
      root.dataset.atmLastSound = kind;
      root.dataset.atmSoundCount = String((+root.dataset.atmSoundCount || 0) + 1);
    };
    let currentPhase = '';
    let phaseSource;
    const endPhase = () => {
      if (phaseSource) { try { phaseSource.stop(); } catch (_) {} }
      phaseSource = null;
    };
    const phaseFor = () => root.classList.contains('atm48-paper-feed') ? 'receipt' :
      root.classList.contains('atm48-printing') ? 'printing' : '';
    const startPhase = phase => {
      if (!soundEnabled || !visible() || context?.state !== 'running' || !phaseBuffers[phase]) return;
      const sample = context.createBufferSource();
      const level = context.createGain();
      sample.buffer = phaseBuffers[phase];
      sample.loop = phase === 'printing';
      level.gain.value = 0.45;
      sample.connect(level);
      level.connect(context.destination);
      active.add(sample);
      phaseSource = sample;
      sample.onended = () => { active.delete(sample); sample.disconnect(); level.disconnect(); };
      sample.start();
      root.dataset[phase === 'printing' ? 'atmPrintingSoundCount' : 'atmReceiptSoundCount'] = String((+root.dataset[phase === 'printing' ? 'atmPrintingSoundCount' : 'atmReceiptSoundCount'] || 0) + 1);
      root.dataset.atmLastSound = phase;
      root.dataset.atmSoundCount = String((+root.dataset.atmSoundCount || 0) + 1);
    };
    const syncPhase = () => {
      const phase = phaseFor();
      if (phase === currentPhase) return;
      currentPhase = phase;
      endPhase();
      if (phase) unlock().then(() => phasesDecoding).then(() => {
        if (currentPhase === phase && phaseFor() === phase) startPhase(phase);
      }).catch(() => {});
    };
    new MutationObserver(syncPhase).observe(root, {attributes:true,attributeFilter:['class']});
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'atm-sound-toggle';
    const updateToggle = () => {
      toggle.textContent = soundEnabled ? 'SOUND ON' : 'SOUND OFF';
      toggle.setAttribute('aria-label', soundEnabled ? 'Turn ATM sound off' : 'Turn ATM sound on');
      toggle.setAttribute('aria-pressed', String(soundEnabled));
    };
    updateToggle();
    root.appendChild(toggle);
    toggle.addEventListener('click', () => {
      soundEnabled = !soundEnabled;
      try { localStorage.setItem('sm-atm-sound', soundEnabled ? 'on' : 'off'); } catch (_) {}
      updateToggle();
      unlock().then(() => tone('toggle')).catch(() => {});
      if (!soundEnabled) {
        active.forEach(sample => { try { sample.stop(); } catch (_) {} });
        phaseSource = null;
      } else unlock().then(() => {
        if (phaseFor()) startPhase(phaseFor());
      }).catch(() => {});
    });
    const controlFor = event => {
      const control = event.target.closest('button,[role="button"],summary,select,input[type="button"],input[type="submit"],input[type="radio"],input[type="checkbox"],a[href]');
      return control && root.contains(control) && !control.disabled && control !== toggle ? control : null;
    };
    document.addEventListener('pointerdown', event => {
      if (root.contains(event.target) || event.target.closest('.sm-home-clothing-toggle')) unlock().catch(() => {});
    }, true);
    document.addEventListener('click', event => {
      const control = controlFor(event);
      if (!control) return;
      lastControl = performance.now();
      blink = 0;
      const kind = control.classList.contains('atm-step-done') ? 'done' : 'button';
      if (context?.state === 'running' && buttonBuffer) tone(kind);
      else unlock().then(() => decoding).then(() => tone(kind)).catch(() => {});
    }, true);
    root.addEventListener('change', event => {
      if (!event.target.matches('select,input[type="range"]')) return;
      lastControl = performance.now();
      unlock().then(() => decoding).then(() => tone('button')).catch(() => {});
    }, true);
    root.addEventListener('animationiteration', event => {
      const backReminder = event.target === root.querySelector('.atm45-back') && event.animationName === 'atmBackPulse' && root.classList.contains('atm-back-needed');
      if (!backReminder && (event.target !== root.querySelector('.atm22-next') || event.animationName !== 'smccDoneBlink')) return;
      if (!(backReminder || ready())) { blink = 0; return; }
      if (++blink % 2 === 0) tone('reminder');
    });
    const stop = () => {
      if (visible()) return;
      blink = 0;
      endPhase();
      active.forEach(oscillator => { try { oscillator.stop(); } catch (_) {} });
    };
    document.addEventListener('visibilitychange', stop);
    window.addEventListener('pagehide', () => {
      active.forEach(oscillator => { try { oscillator.stop(); } catch (_) {} });
    });
    const panel = root.closest('.sm-home-clothing-panel');
    if (panel) new MutationObserver(stop).observe(panel, {attributes:true,attributeFilter:['hidden']});
  });
})();